import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, FormControlLabel, LinearProgress, MenuItem, Stack, Switch, TextField, ToggleButton,
  ToggleButtonGroup, Typography, useTheme,
} from '@mui/material';
import { Add, EventBusyOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useSequentialCreate } from '../../hooks/useSequentialCreate';
import { useTimezone } from '../../hooks/useTimezone';
import holidayService, { Holiday } from '../../services/holidayService';
import SettingsList, { SettingsTile } from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';

type HolidayType = Holiday['type'];

const HOLIDAY_TYPES: Array<{ value: HolidayType; label: string }> = [
  { value: 'NATIONAL', label: 'National/Public Holiday' },
  { value: 'ACADEMIC', label: 'School Holiday' },
  { value: 'RELIGIOUS', label: 'Religious Holiday' },
  { value: 'OTHER', label: 'Other/Emergency' },
];
const typeLabel = (t: string) => HOLIDAY_TYPES.find(o => o.value === t)?.label ?? t;

/** The backend keeps one holiday per date, so a range is one holiday per day; keep ranges sane. */
const MAX_DAYS = 31;

// Dates are handled as school-local 'YYYY-MM-DD' strings — the format the
// API takes — and only formatted through UTC so the browser's zone never shifts them.
const asUTC = (day: string) => new Date(`${day}T00:00:00Z`);
const fmt = (day: string, opts: Intl.DateTimeFormatOptions) => asUTC(day).toLocaleDateString(undefined, { timeZone: 'UTC', ...opts });
const short = (day: string) => fmt(day, { weekday: 'short', day: 'numeric', month: 'short' });
const isDay = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(asUTC(s).getTime());
const daysFrom = (start: string, end: string) => {
  const out: string[] = [];
  for (let t = asUTC(start).getTime(); t <= asUTC(end).getTime() && out.length <= MAX_DAYS; t += 86400000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
};

const emptyForm = { name: '', start: '', end: '', type: 'NATIONAL' as HolidayType, isRecurring: false };

const HolidaySettings: React.FC = () => {
  const theme = useTheme();
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { getToday, getDateFromTimestamp } = useTimezone();
  const writable = canWrite('settings_holidays');
  const bulk = useSequentialCreate();

  const thisYear = Number(getToday().slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  /** The holiday being edited, `'new'` when adding, or null when closed. */
  const [editing, setEditing] = useState<Holiday | 'new' | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHolidays(await holidayService.getHolidays(year));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load holidays', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [year, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const dayOf = useCallback((h: Holiday) => getDateFromTimestamp(h.date), [getDateFromTimestamp]);
  const taken = useMemo(() => new Map(holidays.map(h => [dayOf(h), h.name])), [holidays, dayOf]);
  const tones: Record<HolidayType, string | undefined> = {
    NATIONAL: theme.palette.success.main,
    ACADEMIC: theme.palette.primary.main,
    RELIGIOUS: theme.palette.info.main,
    OTHER: undefined,
  };

  const openNew = () => {
    const today = getToday();
    setForm({ ...emptyForm, start: today.startsWith(String(year)) ? today : `${year}-01-01` });
    setEditing('new');
  };

  const editHoliday = (h: Holiday) => {
    setForm({ name: h.name, start: dayOf(h), end: '', type: h.type, isRecurring: h.isRecurring });
    setEditing(h);
  };

  // A day made from a recurring holiday is edited through the original, so every year stays in step.
  const edit = async (h: Holiday) => {
    if (!h.parentHolidayId) return editHoliday(h);
    try {
      const parent = (await holidayService.getHolidays()).find(p => p._id === h.parentHolidayId);
      if (!parent) {
        enqueueSnackbar('The recurring holiday this day comes from could not be found', { variant: 'error' });
        return;
      }
      editHoliday(parent);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not open this holiday', { variant: 'error' });
    }
  };

  const isNew = editing === 'new';
  const name = form.name.trim();
  const startOk = isDay(form.start);
  const endOk = !form.end || (isDay(form.end) && startOk && form.end >= form.start);
  const days = isNew && startOk && endOk ? daysFrom(form.start, form.end || form.start) : [];
  const tooLong = days.length > MAX_DAYS;
  const skipped = days.filter(d => taken.has(d));
  const fresh = days.filter(d => !taken.has(d));
  const invalid = !name || !startOk || !endOk || (isNew && (tooLong || fresh.length === 0));

  const finish = (startDay: string) => {
    setEditing(null);
    const startYear = Number(startDay.slice(0, 4));
    // Show the year the new holidays went into; a year change reloads by itself.
    if (startYear !== year) setYear(startYear); else load();
  };

  const save = async () => {
    if (!editing || invalid) return;
    const data = { name, type: form.type, isRecurring: form.isRecurring };
    if (editing !== 'new') {
      setSaving(true);
      try {
        await holidayService.updateHoliday(editing._id, { ...data, date: form.start });
        enqueueSnackbar('Holiday saved', { variant: 'success' });
        finish(form.start);
      } catch (e: any) {
        enqueueSnackbar(e?.response?.data?.message || 'Could not save this holiday', { variant: 'error' });
      } finally {
        setSaving(false);
      }
      return;
    }
    const { added, failed } = await bulk.run(fresh, date => holidayService.createHoliday({ ...data, date }));
    if (added > 0) enqueueSnackbar(`${added} holiday${added === 1 ? '' : 's'} added`, { variant: 'success' });
    if (failed.length) {
      enqueueSnackbar(
        `Not added: ${failed.map(f => `${short(f.item)} (${f.message})`).join(', ')}`,
        { variant: 'error', autoHideDuration: 10000 },
      );
    }
    if (added > 0) finish(form.start);
  };

  const yearOptions = Array.from(new Set([thisYear - 1, thisYear, thisYear + 1, year])).sort();

  const preview = () => {
    if (!isNew || !startOk || !endOk) return null;
    if (tooLong) {
      return <Typography variant="body2" color="error">A range can cover up to {MAX_DAYS} days.</Typography>;
    }
    const first = fresh[0];
    const last = fresh[fresh.length - 1];
    return (
      <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'action.hover' }}>
        <Typography variant="body2" fontWeight={600}>
          {fresh.length === 0
            ? 'Every date in this range already has a holiday.'
            : `This adds ${fresh.length} holiday${fresh.length === 1 ? '' : 's'}: ${short(first)}${fresh.length > 1 ? ` – ${short(last)}` : ''}`}
        </Typography>
        {skipped.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            Skipping {skipped.map(d => `${short(d)} (${taken.get(d)})`).join(', ')}, which already {skipped.length === 1 ? 'has a holiday' : 'have holidays'}.
          </Typography>
        )}
        {days.length > 1 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {days.map(d => (
              <Chip
                key={d}
                size="small"
                variant="outlined"
                label={fmt(d, { weekday: 'short', day: 'numeric' })}
                sx={{ borderRadius: '6px', ...(taken.has(d) && { opacity: 0.5, textDecoration: 'line-through' }) }}
              />
            ))}
          </Box>
        )}
        {bulk.progress && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress variant="determinate" value={(bulk.progress.done / bulk.progress.total) * 100} sx={{ borderRadius: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Adding {bulk.progress.done + 1} of {bulk.progress.total}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const isSeries = editing && editing !== 'new' && editing.isRecurring;

  return (
    <>
      <SettingsList
        rows={holidays}
        loading={loading}
        rowKey={h => h._id}
        name={h => h.name}
        subtitle={h => [
          fmt(dayOf(h), { weekday: 'long' }),
          typeLabel(h.type),
          h.parentHolidayId ? 'From a recurring holiday' : h.isRecurring ? 'Repeats every year' : null,
        ].filter(Boolean).join(' · ')}
        leading={h => (
          <SettingsTile color={tones[h.type]}>
            <Stack alignItems="center" sx={{ lineHeight: 1 }}>
              <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.05 }}>{fmt(dayOf(h), { day: 'numeric' })}</Box>
              <Box component="span" sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.1 }}>
                {fmt(dayOf(h), { month: 'short' })}
              </Box>
            </Stack>
          </SettingsTile>
        )}
        muted={h => !!h.parentHolidayId}
        searchText={h => `${h.name} ${typeLabel(h.type)} ${fmt(dayOf(h), { day: 'numeric', month: 'long', weekday: 'long' })}`}
        canWrite={writable}
        onEdit={edit}
        remove={{
          run: h => holidayService.deleteHoliday(h._id),
          noun: 'holiday',
          blocked: h => (h.parentHolidayId ? 'Repeats from a recurring holiday. Delete the original to remove it.' : null),
          consequence: h => (h.isRecurring && !h.parentHolidayId
            ? 'This holiday repeats every year. Deleting it also removes it from every other year. This cannot be undone.'
            : ''),
        }}
        reload={load}
        toolbar={(
          <>
            <ToggleButtonGroup
              value={year}
              exclusive
              size="small"
              onChange={(_, v) => v && setYear(v as number)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.5, fontWeight: 500 } }}
            >
              {yearOptions.map(y => <ToggleButton key={y} value={y}>{y}</ToggleButton>)}
            </ToggleButtonGroup>
            {writable && (
              <Button variant="contained" disableElevation size="small" startIcon={<Add />} onClick={openNew}>
                Add holidays
              </Button>
            )}
          </>
        )}
        empty={{
          icon: <EventBusyOutlined />,
          title: `No holidays in ${year}`,
          description: writable
            ? 'Add a single day or a whole break at once. Recurring holidays fill in every year by themselves.'
            : 'Holidays appear here once someone with access adds them.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title={isNew ? 'Add holidays' : 'Edit holiday'}
        subtitle={isNew
          ? 'Pick one day, or a start and end for a break. Each day becomes its own holiday.'
          : isSeries ? 'This holiday repeats every year. Changes also apply to the coming years.' : undefined}
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving || bulk.busy}
        saveLabel={isNew ? (fresh.length > 1 ? `Add ${fresh.length} holidays` : 'Add holiday') : 'Save'}
        saveDisabled={invalid}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Winter break, Independence Day"
          fullWidth
        />
        <Stack direction="row" spacing={2}>
          <TextField
            label={isNew ? 'From' : 'Date'}
            type="date"
            value={form.start}
            onChange={e => setForm(f => ({ ...f, start: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {isNew && (
            <TextField
              label="Until (optional)"
              type="date"
              value={form.end}
              onChange={e => setForm(f => ({ ...f, end: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: form.start || undefined }}
              error={!endOk}
              helperText={endOk ? ' ' : 'On or after the start'}
              fullWidth
            />
          )}
        </Stack>
        <TextField
          select
          label="Type"
          value={form.type}
          onChange={e => setForm(f => ({ ...f, type: e.target.value as HolidayType }))}
          fullWidth
        >
          {HOLIDAY_TYPES.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <FormControlLabel
          control={<Switch checked={form.isRecurring} onChange={e => setForm(f => ({ ...f, isRecurring: e.target.checked }))} />}
          label="Repeats every year on the same date"
        />
        {preview()}
      </SettingsDialog>
    </>
  );
};

export default HolidaySettings;
